<template>
  <v-form ref="formValid" v-model="valid">
    <v-card
      class="dat-container"
      @drop.prevent="onDrop($event)"
      @dragover.prevent="dragover = true"
      @dragenter.prevent="dragover = true"
      @dragleave.prevent="dragover = false"
    >
      <v-card-text class="content d-flex flex-row">
        <v-col>
          <v-row
            class="d-flex flex-column add-file-container pa-7 mb-0"
            :class="{ 'error-file': !isValidFile() }"
            dense
            align="center"
            justify="center"
          >
            <v-icon class="upload-file" size="45" rounded>
              mdi-file-upload-outline
            </v-icon>
            <p>
              {{ $t('map.tolldomains.geometry.drop') }}
            </p>
            <v-file-input
              id="add-dat-version-file-input"
              ref="fileInputValidation"
              v-model="uploaded"
              class="file-input"
              :color="colors.secondary"
              hide-details
              outlined
              rounded
              dense
              :placeholder="$t('map.tolldomains.geometry.choose')"
              :rules="datfileRules"
              prepend-icon=""
              @change="onFileChange"
            >
              <template v-slot:selection="{}">
                {{ $t('map.tolldomains.geometry.choose') }}
              </template>
            </v-file-input>
          </v-row>
          <v-row v-if="!isValidFile()" class="error-file-text">
            <v-col v-for="(rule, index) in datfileRules" :key="index" class="pt-2 pb-0">
              {{ rule(getFile()) }}
            </v-col>
          </v-row>
        </v-col>
        <v-col>
          <v-row class="file-name-input d-flex flex-column">
            <div class="mb-1 mt-4 file-name">{{ $t('map.tolldomains.dats.list.header_filename') }}</div>
            <v-text-field
              id="add-dat-version-name"
              class="file-name-input"
              outlined
              dark
              dense
              :value="fileData.fileName"
              disabled
              hide-details
            />
          </v-row>
          <v-row class="file-name-input d-flex flex-column">
            <div class="mb-1 file-name">{{ $t('map.tolldomains.dats.list.header_uid') }}</div>
            <v-text-field
              id="add-dat-version-uid"
              v-model="fileData.uid"
              class="file-name-input"
              disabled
              outlined
              dark
              dense
              hide-details="auto"
              :rules="[...datuidRules, isUniqueNumber(datVersionIds, 'common.error.forms.inputs.validator.datid_unicity')]"
            />
          </v-row>
          <v-row class="file-name-input d-flex flex-column">
            <div class="mb-1 file-name">{{ $t('map.tolldomains.dats.list.header_signature') }}</div>
            <v-text-field
              id="add-dat-version-signature"
              v-model="fileData.signature"
              disabled
              outlined
              dark
              dense
              hide-details
            />
          </v-row>
        </v-col>
      </v-card-text>
      <v-divider />
    </v-card>
  </v-form>
</template>

<script lang="ts">
import { Component, Mixins, Prop } from 'vue-property-decorator';
import { colors } from '@/constants/theme/colors';
import Validation from '@/mixins/Validation';
import { DatePicker } from 'ant-design-vue';
import { range } from 'lodash';
import moment from 'moment';

@Component({
  components: {
    DatePicker
  }
})
export default class AddDatVersionDialog extends Mixins(Validation) {
    @Prop()
    private datType!: string;

    @Prop()
    private datnames!: string[];

    @Prop()
    private datId!: number[];

    @Prop()
    private datVersionIds!: number[];

    @Prop()
    private masterAccount!: string;

    @Prop()
    private index!: number;

    private datuidRules = [
      this.requiredRule('common.error.forms.inputs.validator.mandatory')
    ];

    private datfileRules = [
      this.maxFileSize(1032, 'common.error.forms.inputs.validator.datfile_too_large')
    ];

    private colors = colors;

    private dialog = false;

    private uploaded: File|null = null;

    private valid = false;

    private fileData: any = {
      filename: '',
      uid: null,
      signature: '',
      fileData: null
    };

    private onDrop(e: any) {
      if (e.dataTransfer.files.length === 1) {
        this.uploaded = e.dataTransfer.files[0];
        this.onFileChange();
      }
    }

    private saveDatVersion() {
      this.$emit('saveDatVersion', this.fileData);
      this.uploaded = null;
    }

    private close() {
      (this.$refs.formValid as HTMLFormElement).reset();
      this.dialog = false;
    }

    private onFileChange() {
      if (!this.uploaded) return;
      const reader = new FileReader();
      reader.onload = () => {
        const contents = reader.result;
        const view = new DataView(contents as ArrayBuffer);
        // First 2 bytes (UInt16, offset 0) = File ID
        this.fileData.uid = view.getUint16(0);
        // DWord starting at offset 34 (UInt32, offset 34) = File Signature
        // Convert INT to HEX for display purpose
        this.fileData.signature = view.getUint32(34).toString(16).toUpperCase();
        this.fileData.index = this.index;
        this.fileData.fileData = this.uploaded;
        this.fileData.fileName = this.uploaded?.name;
        this.saveDatVersion();
      };
      reader.readAsArrayBuffer(this.uploaded as File);
    }

    private disabledDate(current: moment.Moment) {
      return current.isBefore(moment().startOf('day'));
    }

    private disabledTime(current: moment.Moment) {
      if (current && current.isBefore(moment().startOf('day').add(1, 'day'))) {
        if (current.hours() > moment().hours()) {
          return {
            disabledHours: () => range(0, moment().hours())
          };
        }
        if (current.minutes() === 59) {
          return {
            disabledHours: () => range(0, moment().hours() + 1)
          };
        }
        return {
          disabledHours: () => range(0, moment().hours()),
          disabledMinutes: () => range(0, moment().minutes() + 1)
        };
      }
      return {};
    }

    private isValidFile() {
      return this.$refs.fileInputValidation ? (this.$refs.fileInputValidation as HTMLFormElement).validate() : false;
    }

    private getFile() {
      return this.$refs.fileInputValidation ? (this.$refs.fileInputValidation as HTMLFormElement).value : null;
    }
}
</script>

<style lang="scss" scoped>
@import './styles/add-dat-version.scss';
</style>
